import React, { useMemo, useState } from 'react'
import { IntegrationEmail } from '../../../models/Email'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { selectCurrentUserEmailIntegration, selectEmailContacts } from '../../../store/emailHub/emailUsers'
import { Collapse } from 'react-bootstrap'
import { formatDate, getUserDisplayName } from '../../../apps/professional-app/fa/common/aifeatures/utils'
import { UserAvatar } from '../../../components/UserAvatar/UserAvatar'
import classNames from 'classnames'
import { NavLink } from 'react-router-dom'
import buttonStyles from '../../../components/Button/Button.module.scss'
import { UserTag } from '../../../apps/professional-app/fa/UserTag'
import { LEXICAL_NODES } from '../../../apps/professional-app/fa/MeetingSummary/MeetingResults/Lexical/nodes'
import { emailLexicalTheme } from './emailLexicalTheme'
import { ReadOnlyLexicalEditor } from '../../../apps/professional-app/fa/MeetingSummary/MeetingResults/Lexical/ReadOnlyLexicalEditor/ReadOnlyLexicalEditor'
import { getEditorStateFromHtml, getEditorStateFromText } from '../../../apps/professional-app/fa/MeetingSummary/MeetingResults/Lexical/utils'
import { getInitials } from '../../utils'
import { selectSafeAuthenticatedUser } from '../../../store/auth/authSlice'
import { BusinessEventIcon } from '../../../components/BusinessEvent/BusinessEventIcon/BusinessEventIcon'
import { selectBusinessEventsForEmail } from '../../../store/businessEvent/businessEvent'
import { DetectedBusinessEventTypes } from '../../../components/BusinessEvent/DetectedBusinessEventTypes/DetectedBusinessEventTypes'
import { displayEmailSidebar } from '../../../store/emailHub/emailSidebar'
import { EmailSidebarTitle, EmailSidebarType } from '../EmailSidebar/models'

import './EmailView.scss'

export interface EmailViewProps {
  email: IntegrationEmail
  initialCollapsed?: boolean
}

export const EmailView: React.FC<EmailViewProps> = ({ email, initialCollapsed = true }) => {
  const dispatch = useAppDispatch()

  const contacts = useAppSelector(selectEmailContacts)
  const currentUser = useAppSelector(selectSafeAuthenticatedUser)
  const emailIntegration = useAppSelector(selectCurrentUserEmailIntegration)
  const businessEvents = useAppSelector((state) => selectBusinessEventsForEmail(state, email.externalId))

  const contact = contacts.find((contact) => contact.email?.toLowerCase() === email.sender.email.toLowerCase())

  const [collapsed, setCollapsed] = useState<boolean>(initialCollapsed)

  const name = contact?.name || email.sender.name
  const me = email.sender.email === emailIntegration?.email
  const recipients = [...email.recipients, ...email.ccRecipients]
    .map((recipient) => {
      if (recipient.email === emailIntegration?.email) {
        return 'me'
      }
      const recipientContact = contacts.find((contact) => contact.email?.toLowerCase() === recipient.email.toLowerCase())
      if (recipientContact) {
        return getUserDisplayName(recipientContact)
      }
      return recipient.name || recipient.email
    })
    .join(', ')

  const initialEditorState = useMemo(() => {
    const { content } = email
    if (content['text/html']) {
      return getEditorStateFromHtml(content['text/html'])
    }
    if (content['text/plain']) {
      return getEditorStateFromText(content['text/html'])
    }
    return null
  }, [email])

  const handleDetectedBusinessEventTypesClick = () => {
    dispatch(displayEmailSidebar({ type: EmailSidebarType.BUSINESS_EVENTS, title: EmailSidebarTitle.BUSINESS_EVENTS, icon: '' }))
  }

  return (
    <>
      <div
        className={classNames('d-flex px-3 zocks-pt-0.75 justify-content-between cursor-pointer position-relative', {
          'email-container pb-4': collapsed,
          'border-bottom zocks-border-color-gray-200': collapsed && initialCollapsed,
        })}
        onClick={() => setCollapsed(!collapsed)}
      >
        <div className='d-flex w-100'>
          <div className='d-flex flex-column gap-2 flex-grow-1 mw-0'>
            <div className='d-flex align-items-center flex-grow-1 gap-3'>
              <div className='d-flex align-items-end flex-grow-1 gap-2 justify-content-between email-view-sender-container'>
                <div className='d-flex align-items-center gap-2'>
                  <UserAvatar
                    width='1.5rem'
                    height='1.5rem'
                    fontSize='0.75rem'
                    className={classNames('lead-extra-small zocks-text-white', { 'zocks-bg-color-primary': !!contact, 'zocks-bg-color-gray-900': !contact })}
                    useInitials
                    initialLetters={getInitials(name)}
                  />
                  {contact ? (
                    <NavLink
                      className={`${buttonStyles.button} ${buttonStyles.linkButton} button-link-medium `}
                      to={`/library/manage-contacts/contacts/${contact.id}`}
                      target='_blank'
                    >
                      <div className='paragraph-medium-extended bold text-truncate mw-100'>
                        <div className='text-truncate email-from-text'>{getUserDisplayName(contact)}</div>
                      </div>
                    </NavLink>
                  ) : (
                    <div className='d-flex align-items-center paragraph-medium bold'>{name || (me ? getUserDisplayName(currentUser) : 'Unknown')}</div>
                  )}
                  {me ? <UserTag className='bold' userType='current' /> : null}
                  {!collapsed && <div className='d-flex align-items-center paragraph-medium-extended zocks-text-gray-600'>{`<${email.sender.email}>`}</div>}
                </div>
                {businessEvents.length ? (
                  <div className='d-flex align-items-center zocks-gap-0.125 p-2'>
                    <BusinessEventIcon />
                    <span className='lead-small'>{businessEvents.length}</span>
                  </div>
                ) : null}
              </div>
              <div className='paragraph-small'>{formatDate(email.createdAt)}</div>
            </div>
            {collapsed ? <div className='paragraph-medium-extended zocks-text-gray-600 text-truncate'>{email.snippet}</div> : null}
          </div>
        </div>
      </div>
      <Collapse in={!collapsed}>
        <div>
          <div className={classNames('d-flex flex-column px-3 pb-4 zocks-gap-1.25', { 'border-bottom zocks-border-color-gray-200': initialCollapsed })}>
            <div className='d-flex mt-2 zocks-pl-2.0 paragraph-medium-extended zocks-text-gray-600'>to {recipients}</div>
            <div className='d-flex flex-column'>
              <div className='zocks-pl-2.0'>
                <ReadOnlyLexicalEditor initialEditorState={initialEditorState} lexicalNodes={LEXICAL_NODES} theme={emailLexicalTheme} />
              </div>
              <DetectedBusinessEventTypes businessEvents={businessEvents} handleClick={handleDetectedBusinessEventTypesClick} />
            </div>
          </div>
        </div>
      </Collapse>
    </>
  )
}
